import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  TrendingUp, DollarSign, MapPin, Truck, ArrowRight, 
  Brain, Sparkles, BarChart3, Flame, RefreshCw, Info
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Equipment types
const EQUIPMENT_TYPES = [
  'Dry Van', 'Reefer', 'Flatbed', 'Step Deck', 'Hotshot', 'Box Truck',
  'Sprinter Van', 'Cargo Van', 'Conestoga', 'Lowboy', 'RGN', 'Tanker', 'Power Only'
];

// US States
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const RatePredictorPage = () => {
  const [originState, setOriginState] = useState('');
  const [originCity, setOriginCity] = useState('');
  const [destState, setDestState] = useState('');
  const [destCity, setDestCity] = useState('');
  const [equipment, setEquipment] = useState('Dry Van');
  const [isHazmat, setIsHazmat] = useState(false);
  const [isExpedited, setIsExpedited] = useState(false);
  const [isTeam, setIsTeam] = useState(false);
  
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [marketOverview, setMarketOverview] = useState(null);
  const [hotLanes, setHotLanes] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchMarketData();
  }, []);

  const fetchMarketData = async () => {
    try {
      const [overviewRes, hotsRes, statsRes] = await Promise.all([
        axios.get(`${API}/rate-predictor/market-overview`),
        axios.get(`${API}/rate-predictor/hot-lanes`),
        axios.get(`${API}/rate-predictor/stats`)
      ]);
      setMarketOverview(overviewRes.data);
      setHotLanes(hotsRes.data.hot_lanes || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching market data:', error);
    }
  };

  const predictRate = async () => {
    if (!originState || !destState) {
      toast.error('Please select origin and destination states');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/rate-predictor/predict`, {
        origin_state: originState,
        origin_city: originCity,
        destination_state: destState,
        destination_city: destCity,
        equipment_type: equipment.toLowerCase().replace(' ', '_'),
        is_hazmat: isHazmat,
        is_expedited: isExpedited,
        is_team_driver: isTeam
      });
      
      setPrediction(res.data);
      toast.success('Rate prediction generated!');
    } catch (error) {
      toast.error('Failed to generate prediction');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <Layout>
      <Toaster position="top-right" />
      <div className="p-6 max-w-7xl mx-auto" data-testid="rate-predictor-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Brain className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">AI Rate Predictor</h1>
          <p className="text-gray-400 max-w-xl mx-auto">
            Get AI-powered freight rate predictions based on real market data, lane demand, and seasonal trends.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Prediction Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Input Form */}
            <Card className="bg-slate-800/50 border-slate-700" data-testid="prediction-form">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-purple-500" />
                  Lane Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Origin */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Origin State *</label>
                    <select
                      value={originState}
                      onChange={(e) => setOriginState(e.target.value)}
                      className="w-full bg-slate-700/50 border border-slate-600 text-white rounded-md p-2"
                      data-testid="origin-state-select"
                    >
                      <option value="">Select State</option>
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Origin City (optional)</label>
                    <Input
                      value={originCity}
                      onChange={(e) => setOriginCity(e.target.value)}
                      placeholder="e.g., Los Angeles"
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                </div>

                {/* Destination */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Destination State *</label>
                    <select
                      value={destState}
                      onChange={(e) => setDestState(e.target.value)}
                      className="w-full bg-slate-700/50 border border-slate-600 text-white rounded-md p-2"
                      data-testid="dest-state-select"
                    >
                      <option value="">Select State</option>
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Destination City (optional)</label>
                    <Input
                      value={destCity}
                      onChange={(e) => setDestCity(e.target.value)}
                      placeholder="e.g., Dallas"
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                </div>

                {/* Equipment */}
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Equipment Type</label>
                  <select
                    value={equipment}
                    onChange={(e) => setEquipment(e.target.value)}
                    className="w-full bg-slate-700/50 border border-slate-600 text-white rounded-md p-2"
                    data-testid="equipment-select"
                  >
                    {EQUIPMENT_TYPES.map(eq => <option key={eq} value={eq}>{eq}</option>)}
                  </select>
                </div>

                {/* Special Options */}
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isHazmat}
                      onChange={(e) => setIsHazmat(e.target.checked)}
                      className="rounded bg-slate-700 border-slate-600"
                    />
                    <span className="text-gray-300 text-sm">Hazmat (+25%)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isExpedited}
                      onChange={(e) => setIsExpedited(e.target.checked)}
                      className="rounded bg-slate-700 border-slate-600"
                    />
                    <span className="text-gray-300 text-sm">Expedited (+20%)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isTeam}
                      onChange={(e) => setIsTeam(e.target.checked)}
                      className="rounded bg-slate-700 border-slate-600"
                    />
                    <span className="text-gray-300 text-sm">Team Driver (+15%)</span>
                  </label>
                </div>

                <Button 
                  onClick={predictRate}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 py-6 text-lg"
                  data-testid="predict-btn"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                      Analyzing Market Data...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Get AI Rate Prediction
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Prediction Results */}
            {prediction && (
              <Card className="bg-gradient-to-br from-purple-500/20 to-indigo-500/20 border-purple-500/30" data-testid="prediction-results">
                <CardHeader>
                  <CardTitle className="text-white flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-purple-400" />
                      Rate Prediction
                    </span>
                    <span className={`text-sm px-2 py-1 rounded ${
                      prediction.confidence === 'high' ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'
                    }`}>
                      {prediction.confidence?.toUpperCase()} CONFIDENCE
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Lane Info */}
                  <div className="flex items-center justify-center gap-4 text-lg">
                    <span className="text-white font-medium">{prediction.lane}</span>
                    <Truck className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-400">{prediction.equipment}</span>
                  </div>

                  {/* Rate Range */}
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                      <p className="text-gray-400 text-sm mb-1">Low Estimate</p>
                      <p className="text-2xl font-bold text-amber-400">
                        {formatCurrency(prediction.rate_prediction?.low)}
                      </p>
                    </div>
                    <div className="text-center p-4 bg-purple-500/20 rounded-lg border-2 border-purple-500/50">
                      <p className="text-purple-300 text-sm mb-1">Predicted Rate</p>
                      <p className="text-3xl font-bold text-white">
                        {formatCurrency(prediction.rate_prediction?.predicted)}
                      </p>
                      <p className="text-purple-300 text-sm mt-1">
                        ${prediction.rate_prediction?.per_mile}/mile
                      </p>
                    </div>
                    <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                      <p className="text-gray-400 text-sm mb-1">High Estimate</p>
                      <p className="text-2xl font-bold text-green-400">
                        {formatCurrency(prediction.rate_prediction?.high)}
                      </p>
                    </div>
                  </div>

                  {/* Distance */}
                  <div className="flex justify-center gap-8">
                    <div className="text-center">
                      <p className="text-gray-400 text-sm">Distance</p>
                      <p className="text-xl font-bold text-white">{prediction.distance_miles?.toLocaleString()} miles</p>
                    </div>
                  </div>

                  {/* AI Analysis */}
                  {prediction.ai_analysis && (
                    <div className="p-4 bg-slate-800/50 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Brain className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" />
                        <div>
                          <p className="text-purple-300 font-medium mb-1">AI Market Analysis</p>
                          <p className="text-gray-300 text-sm">{prediction.ai_analysis}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Factors */}
                  <div className="grid sm:grid-cols-4 gap-2 text-center text-sm">
                    <div className="p-2 bg-slate-800/50 rounded">
                      <p className="text-gray-500">Base Rate</p>
                      <p className="text-white">${prediction.factors?.base_rate}/mi</p>
                    </div>
                    <div className="p-2 bg-slate-800/50 rounded">
                      <p className="text-gray-500">Lane Demand</p>
                      <p className={prediction.factors?.lane_demand > 1 ? 'text-green-400' : 'text-white'}>
                        {((prediction.factors?.lane_demand - 1) * 100).toFixed(0)}%
                      </p>
                    </div>
                    <div className="p-2 bg-slate-800/50 rounded">
                      <p className="text-gray-500">Seasonal</p>
                      <p className={prediction.factors?.seasonal > 1 ? 'text-green-400' : 'text-white'}>
                        {((prediction.factors?.seasonal - 1) * 100).toFixed(0)}%
                      </p>
                    </div>
                    <div className="p-2 bg-slate-800/50 rounded">
                      <p className="text-gray-500">Special</p>
                      <p className={prediction.factors?.special_services > 1 ? 'text-amber-400' : 'text-white'}>
                        {((prediction.factors?.special_services - 1) * 100).toFixed(0)}%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Market Data */}
          <div className="space-y-6">
            {/* Market Trend */}
            {marketOverview && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2 text-lg">
                    <BarChart3 className="w-5 h-5 text-blue-500" />
                    Market Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center mb-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      marketOverview.market_trend === 'high' 
                        ? 'bg-green-500/20 text-green-400' 
                        : marketOverview.market_trend === 'low'
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {marketOverview.market_trend?.toUpperCase()} DEMAND
                    </span>
                    <p className="text-gray-400 text-sm mt-2">{marketOverview.current_month}</p>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {marketOverview.market_rates?.slice(0, 6).map((rate, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-slate-700/30 rounded">
                        <span className="text-gray-300 text-sm">{rate.equipment}</span>
                        <span className="text-white font-medium">${rate.current_rate_per_mile}/mi</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Hot Lanes */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2 text-lg">
                  <Flame className="w-5 h-5 text-orange-500" />
                  Hot Lanes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {hotLanes.slice(0, 5).map((lane, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-2 bg-slate-700/30 rounded hover:bg-slate-700/50 cursor-pointer transition-colors"
                      onClick={() => {
                        setOriginState(lane.origin);
                        setDestState(lane.destination);
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{lane.origin}</span>
                        <ArrowRight className="w-4 h-4 text-gray-500" />
                        <span className="text-white font-medium">{lane.destination}</span>
                      </div>
                      <span className="text-green-400 text-sm font-medium">{lane.premium}</span>
                    </div>
                  ))}
                </div>
                <p className="text-gray-500 text-xs mt-3 flex items-center gap-1">
                  <Info className="w-3 h-3" /> Click a lane to auto-fill
                </p>
              </CardContent>
            </Card>

            {/* Stats */}
            {stats && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Prediction Stats</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Total Predictions</span>
                    <span className="text-white font-medium">{stats.total_predictions?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Today</span>
                    <span className="text-white font-medium">{stats.today_predictions}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Avg. Rate</span>
                    <span className="text-green-400 font-medium">${stats.averages?.rate?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Avg. Distance</span>
                    <span className="text-white font-medium">{stats.averages?.distance?.toLocaleString()} mi</span>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RatePredictorPage;
